// AGE's link on an in-app route. `mud-link` draws its anchor inside a shadow
// root, so a bare `href` is a full page load, and react-router's own <Link> is
// a raw `<a>` styled by hand — the one R2 forbids where AGE has the component.
// This keeps AGE's anchor (a copyable address, a middle click, a modifier held
// to open a new tab) and intercepts only the click react-router's <Link>
// intercepts: an unmodified primary one.
import type { ComponentProps } from 'react';
import { useHref, useNavigate } from 'react-router-dom';
import { MudLink } from '@egov-moldova/mud-react';

type MudLinkProps = ComponentProps<typeof MudLink>;
type MudLinkClick = Parameters<NonNullable<MudLinkProps['onClick']>>[0];

export interface RouterLinkProps extends Omit<MudLinkProps, 'href' | 'target' | 'download'> {
  /** The route, as react-router's `to`. */
  to: string;
  /** Replace the current history entry instead of pushing one. */
  replace?: boolean | undefined;
}

export function RouterLink({
  to,
  replace = false,
  onClick,
  children,
  ...rest
}: RouterLinkProps): JSX.Element {
  const navigate = useNavigate();
  const href = useHref(to);

  function handleClick(event: MudLinkClick): void {
    onClick?.(event);
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) {
      return;
    }
    event.preventDefault();
    navigate(to, { replace });
  }

  return (
    <MudLink {...rest} href={href} onClick={handleClick}>
      {children}
    </MudLink>
  );
}
